// @ts-nocheck
// Performance — one row per agent who is logged in right now. Current
// break only (not one row per break event). Team leads pass the roster
// they already have; everyone else reads the real-time agent stream and
// fills in the signed-in agent when that stream does not include them.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ChartCard from './ChartCard';
import { BreakContext } from '@/router/context/BreakContext';
import { useCallStore } from '@/router/zustand/callStore';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';
import { resolveBreakFromRow } from '@/utilities/breakStatus';
import { AGENT_STATES, agentStateKey } from '@/features/cti2/team/teamModel';
import { Pill } from '@/features/cti2/team/teamUi';
import { usePageVisible } from '@/hooks/usePageVisible';

const RECONNECT_MS = 3000;
const REALTIME_URL = `${String(BACKEND_API_BASE_URL || '').replace(/\/+$/, '')}/v2/agents/real-time`;

const STATUS_ORDER = { break: 0, onCall: 1, available: 2 };
const ON_CALL_STATES = new Set(['oncall', 'conference', 'hold', 'connected', 'dialing', 'incoming']);

const COLUMNS = [
  { key: 'agent', label: 'Agent' },
  { key: 'status', label: 'Status' },
  { key: 'break', label: 'Break' },
  { key: 'allowed', label: 'Allowed' },
  { key: 'started', label: 'Started' },
  { key: 'exceeded', label: 'Exceeded' },
];

function pad(value) {
  return String(value).padStart(2, '0');
}

function formatClock(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function formatStarted(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
  });
  const time = date.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${day}, ${time}`;
}

function exceededSeconds(row, nowMs) {
  if (row.statusKey !== 'break' || row.allowedSeconds == null || !row.startedAt) return null;
  const started = new Date(row.startedAt).getTime();
  if (!started) return null;
  return Math.max(0, Math.floor((nowMs - started) / 1000) - row.allowedSeconds);
}

function rowFromRealtime(item) {
  const agent = item?.agent || {};
  const user = item?.user || {};
  const statusKey = agentStateKey(agent, user);
  if (!agent?.id || statusKey === 'offline') return null;
  const onBreak = statusKey === 'break';
  const breakInfo = onBreak
    ? resolveBreakFromRow({ agent, user, activeUserBreak: item.activeUserBreak })
    : null;
  return {
    id: agent.id,
    name: agent.agent_name || user?.name || 'Agent',
    statusKey,
    breakTitle: onBreak ? breakInfo?.title || user?.breakDetail?.title || 'Break' : null,
    allowedSeconds: onBreak ? Number(breakInfo?.durationMinutes || 0) * 60 : null,
    startedAt: onBreak ? breakInfo?.startedAt || null : null,
  };
}

function rowFromRoster(agent) {
  if (!agent?.id || !agent.stateKey || agent.stateKey === 'offline') return null;
  const onBreak = agent.stateKey === 'break';
  return {
    id: agent.id,
    name: agent.name || agent.agent?.agent_name || 'Agent',
    statusKey: agent.stateKey,
    breakTitle: onBreak ? agent.breakInfo?.title || agent.user?.breakDetail?.title || 'Break' : null,
    allowedSeconds: onBreak ? Number(agent.user?.breakDetail?.duration || 0) * 60 : null,
    startedAt: onBreak ? agent.user?.updated_at || null : null,
  };
}

function rowFromSelf(userData, breakCtx, callState) {
  const agent = userData?.agentData;
  if (!agent?.id) return null;
  const onCall = ON_CALL_STATES.has(callState);
  const onBreak = !onCall && breakCtx?.userStatus === 'BREAK' && breakCtx?.activeBreak;
  const statusKey = onCall ? 'onCall' : onBreak ? 'break' : 'available';
  return {
    id: agent.id,
    name: agent.agent_name || userData?.username || 'You',
    statusKey,
    breakTitle: onBreak ? breakCtx.activeBreak.title || 'Break' : null,
    allowedSeconds: onBreak ? Number(breakCtx.activeBreak.duration || 0) * 60 : null,
    startedAt: onBreak ? breakCtx.breakStartTime || null : null,
  };
}

function sortRows(rows) {
  return [...rows].sort((a, b) => {
    const byStatus = (STATUS_ORDER[a.statusKey] ?? 9) - (STATUS_ORDER[b.statusKey] ?? 9);
    if (byStatus) return byStatus;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function useRealtimeAgents(enabled) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return undefined;
    const token = getUserAccessToken();
    let source = null;
    let retry = null;
    let closed = false;
    setLoading(true);

    const connect = () => {
      source = new EventSource(
        `${REALTIME_URL}?token=${encodeURIComponent(token || '')}&x-client-source=${encodeURIComponent(CLIENT_SOURCE)}`,
      );
      source.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const frame = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;
          setItems(Array.isArray(frame?.agents) ? frame.agents : []);
        } catch {
          /* the next frame replaces a bad one */
        }
        setLoading(false);
      };
      source.onerror = () => {
        source.close();
        setLoading(false);
        if (!closed) {
          clearTimeout(retry);
          retry = setTimeout(connect, RECONNECT_MS);
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      if (source) source.close();
    };
  }, [enabled]);

  return { items, loading };
}

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

function BreakTable({ rows, loading }) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const count = rows.length;
  const subtitle = loading
    ? 'Checking who is logged in'
    : count === 1
      ? '1 agent logged in'
      : `${count} agents logged in`;

  return (
    <ChartCard eyebrow="Live" title="Agent break status" subtitle={subtitle}>
      <div className="-m-3 overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.key} style={{ width: `${100 / COLUMNS.length}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className="border-b border-border/70 bg-muted/40 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && count === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-[12px] text-muted-foreground">
                  Loading agent break status…
                </td>
              </tr>
            ) : null}
            {!loading && count === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-[12px] text-muted-foreground">
                  No agents are logged in.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const state = AGENT_STATES[row.statusKey] || AGENT_STATES.available;
              const over = exceededSeconds(row, nowMs);
              const started = formatStarted(row.startedAt);
              return (
                <tr key={row.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2.5 text-[12.5px] font-medium text-foreground">
                    <span className="block truncate" title={row.name}>
                      {row.name}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <Pill tone={state.tone}>{state.label}</Pill>
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px] text-foreground">
                    {row.breakTitle ? (
                      <span className="block truncate" title={row.breakTitle}>
                        {row.breakTitle}
                      </span>
                    ) : (
                      <Dash />
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px] tabular-nums text-foreground">
                    {row.allowedSeconds == null ? <Dash /> : formatClock(row.allowedSeconds)}
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px] text-foreground">
                    {started ? <span className="block truncate">{started}</span> : <Dash />}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-[12.5px] tabular-nums ${
                      over > 0 ? 'font-medium text-destructive' : 'text-foreground'
                    }`}
                  >
                    {over == null ? <Dash /> : formatClock(over)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

// `active` = the table is actually on screen. The real-time stream only runs
// while active and the tab is visible; the last rows stay until it reconnects.
export default function LoggedInAgentBreakTable({ agents, loading = false, userData, active = true }) {
  const rosterMode = Array.isArray(agents);
  const breakCtx = useContext(BreakContext);
  const callState = useCallStore((s) => s.callState);
  const pageVisible = usePageVisible();
  const realtime = useRealtimeAgents(!rosterMode && active && pageVisible);

  const rows = useMemo(() => {
    if (rosterMode) return sortRows(agents.map(rowFromRoster).filter(Boolean));

    const merged = realtime.items.map(rowFromRealtime).filter(Boolean);
    const self = rowFromSelf(userData, breakCtx, callState);
    if (self) {
      const index = merged.findIndex((row) => String(row.id) === String(self.id));
      if (index >= 0) merged[index] = self;
      else merged.push(self);
    }
    return sortRows(merged);
  }, [rosterMode, agents, realtime.items, userData, breakCtx, callState]);

  return <BreakTable rows={rows} loading={rosterMode ? loading && rows.length === 0 : realtime.loading && rows.length === 0} />;
}
